import { format } from 'date-fns'
import { es } from 'date-fns/locale'

export function clp(amount: number): string {
  return amount.toLocaleString('es-CL', { style: 'currency', currency: 'CLP' })
}

export function formatDate(iso: string | Date): string {
  return format(new Date(iso), "dd 'de' MMMM yyyy", { locale: es })
}

export function formatDateShort(iso: string | Date): string {
  return format(new Date(iso), 'dd-MM-yyyy')
}

export function formatTime(iso: string | Date): string {
  return format(new Date(iso), 'HH:mm')
}

export function formatDateTime(iso: string | Date): string {
  return format(new Date(iso), "dd-MM-yyyy HH:mm")
}

export function weekdayName(iso: string | Date): string {
  return format(new Date(iso), 'EEEE d', { locale: es })
}

/** Formatea RUT normalizado "12345678-5" → "12.345.678-5". Un pasaporte (sin guion) se devuelve tal cual. */
export function formatRut(rut: string): string {
  const [body, dv] = rut.split('-')
  if (!body || !dv || !/^\d+$/.test(body)) return rut
  return `${Number(body).toLocaleString('es-CL')}-${dv}`
}

export const APPOINTMENT_STATUS_LABELS: Record<string, string> = {
  REQUESTED: 'Solicitada',
  SCHEDULED: 'Agendada',
  CONFIRMED: 'Confirmada',
  ARRIVED: 'Llegó',
  IN_ROOM: 'En box',
  ATTENDED: 'Atendida',
  CLOSED: 'Cerrada',
  CANCELLED: 'Anulada',
  NO_SHOW: 'No asistió',
}

export const APPOINTMENT_STATUS_COLORS: Record<string, string> = {
  REQUESTED: 'bg-amber-100 text-amber-800',
  // Agendada = neutro (plata); confirmada = celeste de marca. Antes ambas eran
  // azules parecidas y con la paleta nueva se confundían.
  SCHEDULED: 'bg-steel-200 text-steel-800',
  CONFIRMED: 'bg-brand-200 text-brand-800',
  ARRIVED: 'bg-sky-100 text-sky-800',
  IN_ROOM: 'bg-violet-100 text-violet-800',
  ATTENDED: 'bg-emerald-100 text-emerald-800',
  CLOSED: 'bg-gray-200 text-gray-700',
  CANCELLED: 'bg-red-100 text-red-700',
  NO_SHOW: 'bg-orange-100 text-orange-800',
}

/** Por dónde entró la reserva (badge de la agenda y métrica de captación). */
export const APPOINTMENT_CHANNEL_LABELS: Record<string, string> = {
  RECEPTION: 'Mesón',
  WEB: 'Web',
}

export const MODALITY_LABELS: Record<string, string> = {
  IN_PERSON: 'Presencial',
  HOME_VISIT: 'A domicilio',
  TELEMEDICINE: 'Telemedicina',
}

export const HEALTH_ROLE_LABELS: Record<string, string> = {
  PHYSICIAN: 'Médico',
  NURSE: 'Enfermería',
  MEDICAL_TECHNOLOGIST: 'Tecnólogo médico',
  KINESIOLOGIST: 'Kinesiología',
  OCCUPATIONAL_THERAPIST: 'Terapia ocupacional',
}

const INSURANCE_LABELS: Record<string, string> = { FONASA: 'Fonasa', ISAPRE: 'Isapre', PARTICULAR: 'Particular' }

/**
 * Previsión para mostrar: el nombre comercial del catálogo cuando la ficha lo
 * tiene (DT-60), y el agrupador cuando no — una ficha vieja o una isapre
 * escrita a mano no deben quedar en blanco.
 */
export function insuranceLabel(patient: {
  insurance: string
  insuranceProvider?: { name: string } | null
  isapreName?: string | null
}): string {
  if (patient.insuranceProvider) return patient.insuranceProvider.name
  const kind = INSURANCE_LABELS[patient.insurance] ?? patient.insurance
  return patient.isapreName ? `${kind} · ${patient.isapreName}` : kind
}

export const SERVICE_CATEGORY_LABELS: Record<string, string> = {
  CONSULTATION: 'Consulta',
  CONTROL: 'Control',
  PROCEDURE: 'Procedimiento',
  SURGERY: 'Cirugía',
  IMAGING: 'Imagenología',
  LABORATORY: 'Laboratorio',
  REHABILITATION: 'Rehabilitación',
  PREOCCUPATIONAL: 'Preocupacional',
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  CASH: 'Efectivo',
  DEBIT_CARD: 'Débito',
  CREDIT_CARD: 'Crédito',
  TRANSFER: 'Transferencia',
  BONO: 'Bono',
  ONLINE: 'Pago en línea',
}

export const USER_ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administración',
  DOCTOR: 'Profesional',
  RECEPTION: 'Recepción',
  ACCOUNTANT: 'Contabilidad',
  PATIENT: 'Paciente',
  COMPANY: 'Empresa',
}

export const QUOTE_STATUS: Record<string, { label: string; className: string }> = {
  REQUESTED: { label: 'Solicitada', className: 'bg-amber-100 text-amber-800' },
  RESPONDED: { label: 'Respondida', className: 'bg-sky-100 text-sky-800' },
  ACCEPTED: { label: 'Aceptada', className: 'bg-emerald-100 text-emerald-800' },
  REJECTED: { label: 'Rechazada', className: 'bg-red-100 text-red-700' },
  CANCELLED: { label: 'Cancelada', className: 'bg-gray-200 text-gray-600' },
}

export const ORDER_STATUS: Record<string, { label: string; className: string }> = {
  CREATED: { label: 'Creada', className: 'bg-gray-100 text-gray-700' },
  SCHEDULED: { label: 'Agendada', className: 'bg-sky-100 text-sky-800' },
  IN_PROGRESS: { label: 'En proceso', className: 'bg-amber-100 text-amber-800' },
  COMPLETED: { label: 'Completada', className: 'bg-emerald-100 text-emerald-800' },
  CANCELLED: { label: 'Anulada', className: 'bg-red-100 text-red-700' },
}

export const APTITUDE_LABELS: Record<string, string> = {
  PENDING: 'Pendiente',
  FIT: 'Apto',
  FIT_WITH_RESTRICTIONS: 'Apto con restricciones',
  UNFIT: 'No apto',
}

export const APTITUDE_COLORS: Record<string, string> = {
  PENDING: 'bg-gray-100 text-gray-700',
  FIT: 'bg-emerald-100 text-emerald-800',
  FIT_WITH_RESTRICTIONS: 'bg-amber-100 text-amber-800',
  UNFIT: 'bg-red-100 text-red-700',
}

/** Motivos de no atención (H1) — patrón "Motivo de No Atención" del HIS de referencia. */
export const NO_ATTENDANCE_REASON_LABELS: Record<string, string> = {
  PATIENT_NO_SHOW: 'El paciente no llegó',
  PATIENT_LEFT: 'El paciente se retiró antes de ser atendido',
  PROFESSIONAL_ABSENT: 'Ausencia del profesional',
  ADMINISTRATIVE: 'Motivo administrativo',
  OTHER: 'Otro motivo',
}

/** Condición diagnóstica y lateralidad (H3). */
export const CERTAINTY_LABELS: Record<string, string> = {
  HYPOTHESIS: 'Hipótesis diagnóstica',
  CONFIRMED: 'Confirmado',
}

export const LATERALITY_LABELS: Record<string, string> = {
  NOT_APPLICABLE: 'No aplica',
  LEFT: 'Izquierdo',
  RIGHT: 'Derecho',
  BILATERAL: 'Bilateral',
}

/** Documentos clínicos del paciente (P1). */
export const PATIENT_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  LABORATORY: 'Laboratorio',
  IMAGING: 'Imagenología',
  REPORT: 'Informe',
  OTHER: 'Otro',
}

export const PATIENT_DOCUMENT_TYPE_COLORS: Record<string, string> = {
  LABORATORY: 'bg-emerald-100 text-emerald-800',
  IMAGING: 'bg-sky-100 text-sky-800',
  REPORT: 'bg-brand-100 text-brand-800',
  OTHER: 'bg-gray-100 text-gray-700',
}

/**
 * Documentos clínicos emitidos en la atención (H4). Los nombres son los que
 * van impresos en el papel: el paciente busca "receta", no "PRESCRIPTION".
 */
export const CLINICAL_ORDER_TYPE_LABELS: Record<string, string> = {
  PRESCRIPTION: 'Receta médica',
  EXAM_REQUEST: 'Solicitud de exámenes',
  KINESIOLOGY: 'Orden de kinesiología',
}

export const CLINICAL_ORDER_TYPE_COLORS: Record<string, string> = {
  PRESCRIPTION: 'bg-violet-100 text-violet-800',
  EXAM_REQUEST: 'bg-sky-100 text-sky-800',
  KINESIOLOGY: 'bg-emerald-100 text-emerald-800',
}

/** Estados de un próximo control (P2). */
export const FOLLOW_UP_STATUS: Record<string, { label: string; className: string }> = {
  PENDING: { label: 'Por agendar', className: 'bg-amber-100 text-amber-800' },
  SCHEDULED: { label: 'Agendado', className: 'bg-sky-100 text-sky-800' },
  DONE: { label: 'Realizado', className: 'bg-emerald-100 text-emerald-800' },
  CANCELLED: { label: 'Anulado', className: 'bg-red-100 text-red-700' },
}

export const TAX_DOCUMENT_TYPE_LABELS: Record<string, string> = {
  BOLETA: 'Boleta',
  FACTURA: 'Factura',
  BOLETA_HONORARIOS: 'Boleta de honorarios',
  NOTA_CREDITO: 'Nota de crédito',
}

/** Tamaño de archivo legible (los documentos van de KB a decenas de MB). */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
